import { Fragment, useMemo, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, AlertTriangle, Settings2, Plus, Pencil, Trash2, X, ChevronDown, ChevronRight, Check, Search, Plane, Laptop, FileSpreadsheet } from "lucide-react";
import {
  getProjetos, getCategoriasOrcamento,
  getItensCustosGeraisProjeto, getItensRealizadoCustosGeraisProjeto, postItemCustoGeralProjeto, putItemCustoGeralProjeto, deleteItemCustoGeralProjeto,
  getStatusCategorias, putStatusCategoria,
  getTicketsViagemProjeto,
  postGrupoViagemProjeto, putGrupoViagemProjeto, deleteGrupoViagemProjeto,
  getEquipamentosTicketMedio, getAssinaturasTicketMedio, getReconciliacaoKitsAdmissao,
  postGrupoKitAdmissaoProjeto, putGrupoKitAdmissaoProjeto, deleteGrupoKitAdmissaoProjeto,
  getMaoDeObraCustosGeraisProjeto,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import ProjetosCrudModal from "./ProjetosCrudModal";
import { SeletorModoValorViagem, CamposValorTotalViagem, somaValorTotalFaixa, valoresSubcategoriaPayload, faixasValorTotalDoGrupo } from "./ViagemModoValor";
import PerfilAssinaturasKit, { EquipamentosKit, custoEquipamentosKit, estimativaAssinaturasKit, kitExistenteDoGrupo } from "./PerfilAssinaturasKit";
import ReajustesFaixasEditor, { temReajusteIncompleto, reajustesIniciais, reajustesParaPayload } from "./ReajustesFaixasEditor";
import { KpiRowOrcamento, Sparkline, GaugeProgresso, TrendAccessory } from "../dashboard/KpiCard";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";
import {
  TIPOS_LANCAMENTO_OPTIONS, tiposPermitidosParaCategoria, categoriaEhEspecial,
  CODIGO_CATEGORIA_VIAGENS_PREFIXO,
  CODIGOS_SUBCATEGORIAS_VIAGEM_TICKET, CODIGOS_SUBCATEGORIAS_VIAGEM_DIAS, CODIGO_CATEGORIA_PASSAGEM_AEREA, CODIGO_CATEGORIA_VERBA_RELACIONAMENTO,
  CODIGO_CATEGORIA_EQUIPAMENTOS_PREFIXO, EQUIPAMENTOS_KIT_ADMISSAO_PADRAO, NOME_PROJETO_RATEIO_OPERACOES,
} from "../../constants/custosGeraisProjeto";

const DEPARTAMENTO_OPERACAO = "OPERAÇÃO";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  bgActive: isLight ? "#EEF2FF" : "#241A3D",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  categoriaBg: isLight ? "#EDE0FB" : "#251A45",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
const COL_CODIGO_WIDTH = 76;
const COL_ITEM_WIDTH = 200;
const COL_ACOES_WIDTH = 60;

function thStyleTree(C, sticky = null) {
  const base = {
    padding: "8px 8px", fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap",
    borderBottom: `1px solid ${C.border}`, background: C.card,
  };
  if (!sticky) return base;
  return { ...base, position: "sticky", left: sticky, zIndex: 3 };
}

function tdStyleTree(C, strong = false, sticky = null) {
  const base = {
    padding: "6px 8px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
  if (!sticky) return base;
  return { ...base, position: "sticky", left: sticky.left, zIndex: 2, background: sticky.background };
}

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%",
  };
}

function Campo({ label, children, C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>{label}</label>
      {children}
    </div>
  );
}

export default function CustosGeraisProjetoView({ tituloModulo }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [projetos, setProjetos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [itens, setItens] = useState([]);
  const [itensAnoAnterior, setItensAnoAnterior] = useState([]);
  const [statusCategorias, setStatusCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [projetosModalAberto, setProjetosModalAberto] = useState(false);
  const [projetoSelecionadoId, setProjetoSelecionadoId] = useState(null);
  const [modalItem, setModalItem] = useState(null); // { projetoId, categoriaId, itemExistente? }
  const [modalGrupoViagem, setModalGrupoViagem] = useState(null); // { projetoId, grupoExistente? }
  const [modalGrupoKitAdmissao, setModalGrupoKitAdmissao] = useState(null); // { projetoId, grupoExistente? }

  // Mão de Obra (2.1/2.2 ratada por AlocacaoPessoal) — só leitura, entra junto da tabela
  // "Itens de Custos Gerais" (ver arvoreMaoDeObraParaCategorias/ProjetoAccordion). Carregamento
  // isolado do resto da tela (não trava o accordion de categorias se falhar) — as linhas só
  // aparecem quando chegam, sem spinner próprio.
  const [maoDeObra, setMaoDeObra] = useState(null);
  const [maoDeObraErro, setMaoDeObraErro] = useState("");
  // Mão de Obra agregada (todos os projetos, sem rateio) — usada só pelo Consolidado abaixo.
  const [maoDeObraConsolidado, setMaoDeObraConsolidado] = useState(null);

  async function carregarProjetos() {
    setProjetos(await getProjetos());
  }

  async function carregarTudo() {
    setLoading(true);
    setErro("");
    try {
      const [ps, cats, its, itsAnoAnterior, status] = await Promise.all([
        getProjetos(),
        getCategoriasOrcamento(DEPARTAMENTO_OPERACAO),
        getItensCustosGeraisProjeto(ano),
        getItensRealizadoCustosGeraisProjeto(ano - 1),
        getStatusCategorias(ano),
      ]);
      setProjetos(ps || []);
      setCategorias(cats || []);
      setItens(its || []);
      setItensAnoAnterior(itsAnoAnterior || []);
      setStatusCategorias(status || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Marca/desmarca "já terminei de preencher esta categoria" (categoria mãe) para 1 projeto —
  // flag manual, por (categoria, ano, projeto); atualização otimista, sem recarregar tudo.
  async function alternarStatusCategoria(categoriaId, concluido, projetoId) {
    try {
      await putStatusCategoria(categoriaId, ano, concluido, projetoId);
      setStatusCategorias((lista) => {
        const existe = lista.some((s) => s.categoria_id === categoriaId && s.projeto_id === projetoId);
        if (existe) {
          return lista.map((s) => (s.categoria_id === categoriaId && s.projeto_id === projetoId ? { ...s, concluido } : s));
        }
        return [...lista, { categoria_id: categoriaId, ano, projeto_id: projetoId, concluido }];
      });
    } catch (e) {
      setErro(e.message);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarTudo(); }, [ano]);

  useEffect(() => {
    if (!projetoSelecionadoId) { setMaoDeObra(null); setMaoDeObraErro(""); return; }
    let cancelado = false;
    setMaoDeObraErro("");
    getMaoDeObraCustosGeraisProjeto(ano, projetoSelecionadoId)
      .then((dados) => { if (!cancelado) setMaoDeObra(dados); })
      .catch(() => { if (!cancelado) setMaoDeObraErro("Não foi possível carregar a mão de obra deste projeto."); });
    return () => { cancelado = true; };
  }, [ano, projetoSelecionadoId]);

  // Agregado (todos os projetos, sem rateio) — sem projeto_id, o backend devolve o total do
  // departamento inteiro. Usado só pelo Consolidado (independe do projeto selecionado acima).
  useEffect(() => {
    let cancelado = false;
    getMaoDeObraCustosGeraisProjeto(ano)
      .then((dados) => { if (!cancelado) setMaoDeObraConsolidado(dados); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [ano]);

  const projetosAtivos = useMemo(() => projetos.filter((p) => p.ativo), [projetos]);
  const categoriaPorId = useMemo(() => Object.fromEntries(categorias.map((c) => [c.id, c])), [categorias]);

  // Progresso (categorias mãe lançáveis concluídas) por projeto — alimenta o select de projetos
  // (verde quando 100%) sem depender do accordion estar montado.
  const progressoPorProjeto = useMemo(() => {
    const pais = categorias.filter((c) => !c.categoria_pai_id);
    const resultado = {};
    projetosAtivos.forEach((p) => {
      const ehRateio = p.nome === NOME_PROJETO_RATEIO_OPERACOES;
      const lancaveis = pais.filter((c) => ehRateio || c.codigo !== CODIGO_CATEGORIA_EQUIPAMENTOS_PREFIXO);
      const concluidasIds = new Set(statusCategorias.filter((s) => s.projeto_id === p.id && s.concluido).map((s) => s.categoria_id));
      const concluidas = lancaveis.filter((c) => concluidasIds.has(c.id)).length;
      resultado[p.id] = { total: lancaveis.length, concluidas, completo: lancaveis.length > 0 && concluidas === lancaveis.length };
    });
    return resultado;
  }, [categorias, projetosAtivos, statusCategorias]);

  const projetoSelecionado = projetosAtivos.find((p) => p.id === projetoSelecionadoId) || null;

  // Item comum é sempre 1 linha (1 id), com N faixas de vigência dentro dela — "adicionar
  // período" nunca cria uma linha nova, só mais uma faixa na mesma linha.
  // Erro de API não é capturado aqui de propósito — sobe para o catch local do próprio modal
  // (ModalItem.salvar), que exibe a mensagem dentro do modal (por cima do banner desta tela).
  async function criarItem(dados) {
    const criado = await postItemCustoGeralProjeto(dados);
    setItens((lista) => [...lista, criado]);
    setModalItem(null);
  }

  async function atualizarItem(id, dados) {
    const atualizado = await putItemCustoGeralProjeto(id, dados);
    setItens((lista) => lista.map((i) => (i.id === id ? atualizado : i)));
    setModalItem(null);
  }

  async function excluirItem(item) {
    if (!window.confirm(`Excluir o item "${item.item_nome}"? Isso remove todos os períodos lançados para ele. Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteItemCustoGeralProjeto(item.id);
      setItens((lista) => lista.filter((i) => i.id !== item.id));
    } catch (e) {
      setErro(e.message);
    }
  }

  // Viagem é sempre N linhas fixas (uma por subcategoria aplicável), unidas por item_grupo_id —
  // "adicionar período" nunca cria linha nova, só mais uma faixa nas linhas do grupo;
  // editar/excluir afeta todas juntas. item_grupo_id (não tipo_lancamento) é o discriminador,
  // pois a linha opcional de Verba de Relacionamento também pertence ao grupo mas continua
  // tipo_lancamento="valor_manual" (não usa ticket médio).
  function abrirEdicaoItem(item) {
    if (item.item_grupo_id) {
      const itensGrupo = itens.filter((i) => i.item_grupo_id === item.item_grupo_id);
      if (itensGrupo.some((i) => i.tipo_lancamento === "kit_admissao")) {
        const kit = kitExistenteDoGrupo(itensGrupo);
        const faixa = kit?.ancora.faixas?.[0];
        setModalGrupoKitAdmissao({
          projetoId: item.projeto_id,
          grupoExistente: {
            item_grupo_id: item.item_grupo_id, item_nome: kit ? kit.ancora.item_nome.replace(/ — .*$/, "") : "",
            tipo_kit: kit?.tipo_kit || "atendimento", mes: faixa?.mes_inicio || 1, quantidade: faixa?.usuarios || 1,
            assinaturas: kit?.assinaturas || [], equipamentos: kit?.equipamentos || [],
          },
        });
        return;
      }
      const itemVerba = itensGrupo.find((i) => categoriaPorId[i.categoria_id]?.codigo === CODIGO_CATEGORIA_VERBA_RELACIONAMENTO);
      const linhasTicket = itensGrupo.filter((i) => CODIGOS_SUBCATEGORIAS_VIAGEM_TICKET.includes(categoriaPorId[i.categoria_id]?.codigo));
      // Modo "Custo total": as linhas de subcategoria são valor_manual (nenhuma 'viagem' no grupo).
      const modoValor = linhasTicket.some((i) => i.tipo_lancamento === "viagem") ? "ticket" : "valor_total";
      const itemTicket = linhasTicket.find((i) => i.tipo_lancamento === "viagem") || linhasTicket[0] || item;
      const temPassagemAerea = itensGrupo.some((i) => categoriaPorId[i.categoria_id]?.codigo === CODIGO_CATEGORIA_PASSAGEM_AEREA);
      const faixas = modoValor === "valor_total"
        ? faixasValorTotalDoGrupo(
          linhasTicket.map((i) => ({ codigo: categoriaPorId[i.categoria_id]?.codigo, faixas: i.faixas })),
          itemVerba?.faixas,
        )
        : itemTicket.faixas.map((f, idx) => ({ ...f, verba_relacionamento: itemVerba?.faixas[idx]?.valor ?? 0 }));
      setModalGrupoViagem({
        projetoId: item.projeto_id,
        grupoExistente: {
          item_grupo_id: item.item_grupo_id, item_nome: itemTicket.item_nome, faixas, modoValor,
          incluirPassagemAerea: temPassagemAerea,
          incluirVerbaRelacionamento: !!itemVerba,
        },
      });
    } else {
      setModalItem({
        projetoId: item.projeto_id, categoriaId: item.categoria_id,
        itemExistente: { id: item.id, item_nome: item.item_nome, tipo_lancamento: item.tipo_lancamento, faixas: item.faixas },
      });
    }
  }

  async function excluirItemOuGrupo(item) {
    if (!item.item_grupo_id) return excluirItem(item);
    const ehKit = item.tipo_lancamento === "kit_admissao";
    const mensagem = ehKit
      ? `Excluir o Kit Admissão "${item.item_nome.replace(/ — .*$/, "")}"? Isso remove todas as linhas do kit (Fone, Teclado, Mouse, Notebook, Monitor e assinaturas). Essa ação não pode ser desfeita.`
      : `Excluir o lançamento de viagem "${item.item_nome}"? Isso remove o custo em todas as subcategorias vinculadas (Alimentação, Hospedagem, Passagem Aérea, Transporte e/ou Verba de Relacionamento). Essa ação não pode ser desfeita.`;
    if (!window.confirm(mensagem)) return;
    try {
      if (ehKit) await deleteGrupoKitAdmissaoProjeto(item.item_grupo_id);
      else await deleteGrupoViagemProjeto(item.item_grupo_id);
      setItens((lista) => lista.filter((i) => i.item_grupo_id !== item.item_grupo_id));
    } catch (e) {
      setErro(e.message);
    }
  }

  // Idem: erro sobe para o catch local do ModalGrupoViagem.salvar, não é engolido aqui.
  async function criarGrupoViagem(dados) {
    const criados = await postGrupoViagemProjeto(dados);
    setItens((lista) => [...lista, ...criados]);
    setModalGrupoViagem(null);
  }

  async function atualizarGrupoViagem(itemGrupoId, dados) {
    const atualizados = await putGrupoViagemProjeto(itemGrupoId, dados);
    setItens((lista) => [...lista.filter((i) => i.item_grupo_id !== itemGrupoId), ...atualizados]);
    setModalGrupoViagem(null);
  }

  // Idem: erro sobe para o catch local do ModalGrupoKitAdmissao.salvar, não é engolido aqui.
  async function criarGrupoKitAdmissao(dados) {
    const criados = await postGrupoKitAdmissaoProjeto(dados);
    setItens((lista) => [...lista, ...criados]);
    setModalGrupoKitAdmissao(null);
  }

  async function atualizarGrupoKitAdmissao(itemGrupoId, dados) {
    const atualizados = await putGrupoKitAdmissaoProjeto(itemGrupoId, dados);
    setItens((lista) => [...lista.filter((i) => i.item_grupo_id !== itemGrupoId), ...atualizados]);
    setModalGrupoKitAdmissao(null);
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{tituloModulo} · OPERAÇÃO · {ano}</h2>
            <button onClick={() => setProjetosModalAberto(true)} style={btnStyle(C.brand, true)}>
              <Settings2 size={15} /> Gerenciar Projetos
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
          ) : projetosAtivos.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              Nenhum projeto ativo cadastrado — clique em "Gerenciar Projetos" para criar um.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SeletorProjeto
                C={C} projetos={projetosAtivos} progressoPorProjeto={progressoPorProjeto}
                selecionadoId={projetoSelecionadoId} onSelecionar={setProjetoSelecionadoId}
              />
              {projetoSelecionado ? (
                <ProjetoAccordion
                  key={projetoSelecionado.id}
                  C={C}
                  projeto={projetoSelecionado}
                  ano={ano}
                  itens={itens.filter((i) => i.projeto_id === projetoSelecionado.id)}
                  itensAnoAnterior={itensAnoAnterior.filter((i) => i.projeto_id === projetoSelecionado.id)}
                  statusCategorias={statusCategorias.filter((s) => s.projeto_id === projetoSelecionado.id)}
                  onToggleStatus={(categoriaId, concluido) => alternarStatusCategoria(categoriaId, concluido, projetoSelecionado.id)}
                  categorias={categorias}
                  onNovoItem={(categoriaId) => setModalItem({ projetoId: projetoSelecionado.id, categoriaId })}
                  onEditarItem={abrirEdicaoItem}
                  onExcluirItem={excluirItemOuGrupo}
                  onNovoGrupoViagem={() => setModalGrupoViagem({ projetoId: projetoSelecionado.id })}
                  onNovoGrupoKitAdmissao={() => setModalGrupoKitAdmissao({ projetoId: projetoSelecionado.id })}
                  maoDeObra={maoDeObra}
                  maoDeObraErro={maoDeObraErro}
                />
              ) : (
                <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px dashed ${C.border}`, borderRadius: 14 }}>
                  Selecione um projeto acima para lançar e visualizar os custos.
                </div>
              )}
            </div>
          )}

          {!loading && (
            <SecaoConsolidado C={C} ano={ano} categorias={categorias} itens={itens} projetos={projetos} maoDeObra={maoDeObraConsolidado} />
          )}
        </main>
      </div>

      {modalItem && (
        <ModalItem
          C={C}
          ano={ano}
          projetoId={modalItem.projetoId}
          categoria={categoriaPorId[modalItem.categoriaId]}
          itemExistente={modalItem.itemExistente}
          onClose={() => setModalItem(null)}
          onCriar={criarItem}
          onAtualizar={atualizarItem}
        />
      )}

      {modalGrupoViagem && (
        <ModalGrupoViagem
          C={C}
          ano={ano}
          projetoId={modalGrupoViagem.projetoId}
          grupoExistente={modalGrupoViagem.grupoExistente}
          onClose={() => setModalGrupoViagem(null)}
          onCriar={criarGrupoViagem}
          onAtualizar={atualizarGrupoViagem}
        />
      )}

      {modalGrupoKitAdmissao && (
        <ModalGrupoKitAdmissao
          C={C}
          ano={ano}
          projetoId={modalGrupoKitAdmissao.projetoId}
          grupoExistente={modalGrupoKitAdmissao.grupoExistente}
          onClose={() => setModalGrupoKitAdmissao(null)}
          onCriar={criarGrupoKitAdmissao}
          onAtualizar={atualizarGrupoKitAdmissao}
        />
      )}

      {projetosModalAberto && (
        <ProjetosCrudModal C={C} projetos={projetos} onClose={() => setProjetosModalAberto(false)} onProjetosChange={carregarProjetos} />
      )}
    </div>
  );
}

// Monta a árvore Categoria Pai > Subcategoria > Itens, com totais mensais, para um projeto —
// usada tanto pelos botões-KPI de lançamento quanto pela tabela somente-leitura abaixo deles.
function construirArvoreCategorias(categorias, itens) {
  const ordenar = (lista) => [...lista].sort((a, b) => (a.ordem - b.ordem) || a.codigo.localeCompare(b.codigo));
  const pais = ordenar(categorias.filter((c) => !c.categoria_pai_id));
  return pais.map((pai) => {
    const subs = ordenar(categorias.filter((c) => c.categoria_pai_id === pai.id)).map((sub) => {
      const itensSub = itens.filter((i) => i.categoria_id === sub.id);
      const mensal = Array(12).fill(0);
      itensSub.forEach((item) => { for (let m = 1; m <= 12; m++) mensal[m - 1] += item.valores_mensais[m] || 0; });
      return { categoria: sub, itens: itensSub, mensal, total: mensal.reduce((a, b) => a + b, 0) };
    });
    const mensal = Array(12).fill(0);
    subs.forEach((s) => s.mensal.forEach((v, i) => { mensal[i] += v; }));
    return { categoria: pai, subcategorias: subs, mensal, total: mensal.reduce((a, b) => a + b, 0) };
  });
}

// Converte a árvore de Mão de Obra (formato grupo → subgrupo → conta, vinda de
// getMaoDeObraCustosGeraisProjeto — ver backend/services/mao_de_obra_svc.py) para o mesmo
// formato { categoria, subcategorias, mensal, total } de construirArvoreCategorias, pra poder
// misturar as duas na mesma tabela "Itens de Custos Gerais"/Consolidado. Cada subgrupo (2.1/2.2)
// vira uma "categoria pai" e cada conta-filha (2.1.01, 2.1.02...) vira uma "subcategoria" — só
// que sem itens editáveis (computado: true), já que o valor vem calculado de Alocação de
// Pessoal, não de lançamento manual.
function arvoreMaoDeObraParaCategorias(arvoreMaoDeObra) {
  const subgrupos = arvoreMaoDeObra?.filhos || [];
  return subgrupos.map((subgrupo) => {
    const [codigo, ...resto] = subgrupo.label.split(" ");
    const subcategorias = (subgrupo.filhos || []).map((conta) => {
      const [codigoConta, ...restoConta] = conta.label.split(" ");
      const mensal = conta.meses.map((m) => m.planejado);
      return {
        categoria: { id: conta.id, codigo: codigoConta, nome: restoConta.join(" ") },
        itens: [], computado: true,
        mensal, total: conta.total.planejado,
      };
    });
    const mensal = subgrupo.meses.map((m) => m.planejado);
    return {
      categoria: { id: subgrupo.id, codigo, nome: resto.join(" ") },
      subcategorias, computado: true,
      mensal, total: subgrupo.total.planejado,
    };
  });
}

// Total mensal (12 posições) somando todas as categorias mãe da árvore — usado nos
// sparklines do KPI row do projeto (a árvore já soma por categoria, não existe um total
// mensal "achatado" pronto).
function mensalDaArvore(arvore) {
  const mensal = Array(12).fill(0);
  arvore.forEach((pai) => pai.mensal.forEach((v, i) => { mensal[i] += v; }));
  return mensal;
}

// Select com filtro de texto (combobox) — substitui a lista longa de accordions. Projetos com
// todas as categorias mãe concluídas aparecem em verde.
function SeletorProjeto({ C, projetos, progressoPorProjeto, selecionadoId, onSelecionar }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function fora(e) { if (ref.current && !ref.current.contains(e.target)) setAberto(false); }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const selecionado = projetos.find((p) => p.id === selecionadoId);
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return [...projetos]
      .filter((p) => !termo || p.nome.toLowerCase().includes(termo))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [projetos, busca]);
  const completoSel = selecionado && progressoPorProjeto[selecionado.id]?.completo;
  const corSel = completoSel ? C.success : C.border;

  return (
    <div ref={ref} style={{ position: "relative", maxWidth: 520 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>Projeto</label>
      <button
        onClick={() => { setAberto((a) => !a); setBusca(""); }}
        style={{
          width: "100%", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          background: completoSel ? `${C.success}18` : C.card, border: `1px solid ${corSel}`, borderRadius: 10,
          padding: "10px 14px", fontSize: 14, fontWeight: 700, color: completoSel ? C.success : C.text, cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {completoSel && <Check size={16} />}
          {selecionado ? selecionado.nome : "Selecione um projeto…"}
        </span>
        <ChevronDown size={16} color={C.muted} />
      </button>

      {aberto && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 20, background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", overflow: "hidden",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${C.border}` }}>
            <Search size={14} color={C.muted} />
            <input
              autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Filtrar projeto…"
              style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: C.text, fontSize: 13 }}
            />
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {filtrados.length === 0 ? (
              <div style={{ padding: 14, fontSize: 13, color: C.muted, textAlign: "center" }}>Nenhum projeto encontrado.</div>
            ) : filtrados.map((p) => {
              const prog = progressoPorProjeto[p.id];
              const completo = prog?.completo;
              return (
                <div
                  key={p.id}
                  onClick={() => { onSelecionar(p.id); setAberto(false); }}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "9px 14px", cursor: "pointer",
                    fontSize: 13, fontWeight: p.id === selecionadoId ? 700 : 500,
                    color: completo ? C.success : C.text, background: p.id === selecionadoId ? `${C.brand}14` : "transparent",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = completo ? `${C.success}18` : `${C.brand}14`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = p.id === selecionadoId ? `${C.brand}14` : "transparent"; }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>{completo && <Check size={14} />}{p.nome}</span>
                  <span style={{ fontSize: 11, color: completo ? C.success : C.muted }}>{prog ? `${prog.concluidas}/${prog.total}` : ""}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjetoAccordion({
  C, projeto, ano, itens, itensAnoAnterior, statusCategorias, onToggleStatus, categorias,
  onNovoItem, onEditarItem, onExcluirItem, onNovoGrupoViagem, onNovoGrupoKitAdmissao,
  maoDeObra, maoDeObraErro,
}) {
  const arvore = useMemo(() => construirArvoreCategorias(categorias, itens), [categorias, itens]);
  const arvoreAnoAnterior = useMemo(() => construirArvoreCategorias(categorias, itensAnoAnterior), [categorias, itensAnoAnterior]);
  // Mão de Obra (2.1/2.2, computada de Alocação de Pessoal) entra JUNTO da tabela "Itens de
  // Custos Gerais" — não é mais uma seção à parte — como categorias extras no topo, marcadas
  // "computado" (ver arvoreMaoDeObraParaCategorias). Não entra em arvoreLancavel/KPIs/progresso
  // (isso continua só com categorias reais — Mão de Obra nunca é lançável manualmente aqui).
  const arvoreTabela = useMemo(
    () => [...arvoreMaoDeObraParaCategorias(maoDeObra), ...arvore],
    [maoDeObra, arvore]
  );
  // Equipamentos e Materiais (2.7) só é lançável dentro do projeto "Rateio Operações" — em
  // qualquer outro projeto some da lista de categorias lançáveis (mas continua aparecendo na
  // tabela somente-leitura abaixo, caso haja lançamento legado). Backend força o mesmo destino
  // mesmo que o front tente mandar outro projeto, então isto é só UX, não a trava de verdade.
  const ehRateioOperacoes = projeto.nome === NOME_PROJETO_RATEIO_OPERACOES;
  const arvoreLancavel = useMemo(
    () => (ehRateioOperacoes ? arvore : arvore.filter((pai) => pai.categoria.codigo !== CODIGO_CATEGORIA_EQUIPAMENTOS_PREFIXO)),
    [arvore, ehRateioOperacoes]
  );
  const totalAno = useMemo(() => arvore.reduce((acc, pai) => acc + pai.total, 0), [arvore]);
  const totalAnoAnterior = useMemo(() => arvoreAnoAnterior.reduce((acc, pai) => acc + pai.total, 0), [arvoreAnoAnterior]);
  const mensalAnoAnterior = useMemo(() => mensalDaArvore(arvoreAnoAnterior), [arvoreAnoAnterior]);
  const mensalAno = useMemo(() => mensalDaArvore(arvore), [arvore]);
  // Mão de Obra do projeto em card próprio ("Orçado Mão de Obra"). Não há card de realizado/
  // variação de Mão de Obra aqui: o realizado só existe por departamento (OPERAÇÃO inteira,
  // sem quebra por projeto — ver RealizadoMaoDeObraAnual).
  const arvoreMaoDeObra = useMemo(() => arvoreMaoDeObraParaCategorias(maoDeObra), [maoDeObra]);
  const totalMaoDeObra = useMemo(() => arvoreMaoDeObra.reduce((acc, pai) => acc + pai.total, 0), [arvoreMaoDeObra]);
  const mensalMaoDeObra = useMemo(() => mensalDaArvore(arvoreMaoDeObra), [arvoreMaoDeObra]);
  const [escolha, setEscolha] = useState(null); // paiLinha da árvore, quando um botão-KPI é clicado
  const [escolhaGenericaAberta, setEscolhaGenericaAberta] = useState(false);

  const statusPorCategoriaId = useMemo(
    () => Object.fromEntries((statusCategorias || []).map((s) => [s.categoria_id, s.concluido])),
    [statusCategorias]
  );
  // Progresso conta só categorias lançáveis neste projeto — sem isto, projetos que não são
  // Rateio Operações nunca chegariam a 100% (Equipamentos nunca teria como ser marcada concluída).
  const totalCategoriasPai = arvoreLancavel.length;
  const categoriasConcluidas = arvoreLancavel.filter((l) => statusPorCategoriaId[l.categoria.id]).length;
  const percentProgresso = totalCategoriasPai > 0 ? (categoriasConcluidas / totalCategoriasPai) * 100 : 0;
  const projetoCompleto = totalCategoriasPai > 0 && categoriasConcluidas === totalCategoriasPai;
  const variacaoAnual = totalAno - totalAnoAnterior;
  const direcaoVariacao = variacaoAnual > 0 ? "up" : variacaoAnual < 0 ? "down" : "flat";
  const percentVariacao = totalAnoAnterior > 0 ? (variacaoAnual / totalAnoAnterior) * 100 : 0;

  const kpiItemsProjeto = [
    { label: `Orçado ${ano}`, value: formatCurrency(totalAno), background: C.brand, sub: "Demais custos do projeto (exceto mão de obra)", accessory: mensalAno.some((v) => v) && <Sparkline data={mensalAno} color="#fff" /> },
    {
      label: `Realizado ${ano - 1}`, value: formatCurrency(totalAnoAnterior), color: C.muted,
      // Ano ainda em andamento: só os meses fechados são realizado de fato, o resto é projeção.
      ...(ano - 1 === ANO_ATUAL ? { sub: "Realizado dos meses fechados + projeção dos meses restantes até dezembro" } : {}),
      accessory: mensalAnoAnterior.some((v) => v) && <Sparkline data={mensalAnoAnterior} color={C.muted} />,
    },
    {
      label: "Progresso Orçamentário", value: `${categoriasConcluidas}/${totalCategoriasPai}`, sub: "categorias mãe concluídas",
      ...(projetoCompleto ? { background: C.success } : {}),
      accessory: <GaugeProgresso percent={percentProgresso} color={projetoCompleto ? "#fff" : C.brand} trackColor={projetoCompleto ? "rgba(255,255,255,0.35)" : C.border} />,
    },
    {
      label: "Variação Anual", value: `${variacaoAnual > 0 ? "+" : ""}${formatCurrency(variacaoAnual)}`,
      color: direcaoVariacao === "up" ? C.success : direcaoVariacao === "down" ? C.danger : C.text,
      sub: `vs. ${ano - 1}: ${variacaoAnual === 0 ? "Sem alteração" : `${percentVariacao > 0 ? "+" : ""}${percentVariacao.toFixed(1)}%`}`,
      accessory: <TrendAccessory direcao={direcaoVariacao} C={C} />,
    },
  ];
  // 2ª linha: Mão de Obra sob o card "Orçado" (demais células vazias, para alinhar as colunas).
  const kpiItemsMaoDeObra = [
    {
      label: `Orçado Mão de Obra ${ano}`, value: maoDeObra ? formatCurrency(totalMaoDeObra) : "—",
      background: C.brand, sub: "Salários, encargos e benefícios do projeto",
      accessory: mensalMaoDeObra.some((v) => v) && <Sparkline data={mensalMaoDeObra} color="#fff" />,
    },
    null, null, null,
  ];

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: projetoCompleto ? C.success : C.text }}>{projeto.nome}</span>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.brand }}>{formatCurrency(totalAno)} / ano</span>
      </div>

      {(
        <div style={{ padding: "0 18px 18px", display: "flex", flexDirection: "column", gap: 20 }}>
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRowOrcamento items={kpiItemsProjeto} C={C} />
            <KpiRowOrcamento items={kpiItemsMaoDeObra} C={C} />
          </section>
          <section>
            <h3 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Lançar Custo</h3>
            <BotoesCategoriaProjeto
              C={C} arvore={arvoreLancavel} onSelecionar={setEscolha}
              onLancarGenerico={() => setEscolhaGenericaAberta(true)}
              statusPorCategoriaId={statusPorCategoriaId}
              onToggleStatus={onToggleStatus}
            />
          </section>
          <section>
            <h3 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Itens de Custos Gerais</h3>
            {maoDeObraErro && (
              <p style={{ margin: "0 0 8px", fontSize: 11, color: C.danger }}>{maoDeObraErro}</p>
            )}
            <TabelaItensProjeto C={C} arvore={arvoreTabela} onEditarItem={onEditarItem} onExcluirItem={onExcluirItem} />
          </section>
        </div>
      )}

      {escolhaGenericaAberta && (
        <ModalEscolherCategoriaPaiProjeto
          C={C}
          arvore={arvoreLancavel}
          onClose={() => setEscolhaGenericaAberta(false)}
          onEscolher={(paiLinha) => { setEscolhaGenericaAberta(false); setEscolha(paiLinha); }}
        />
      )}

      {escolha && (
        <ModalEscolherSubcategoriaProjeto
          C={C}
          paiLinha={escolha}
          onClose={() => setEscolha(null)}
          onEscolherSubcategoria={(categoriaId) => { setEscolha(null); onNovoItem(categoriaId); }}
          onLancarViagem={() => { setEscolha(null); onNovoGrupoViagem(); }}
          onLancarKitAdmissao={() => { setEscolha(null); onNovoGrupoKitAdmissao(); }}
        />
      )}
    </div>
  );
}

// Botões cheios (fundo roxo sólido) — 1 genérico "Lançar Despesa" (abre o seletor de
// categoria mãe deste projeto) seguido de 1 por categoria mãe; clicar em uma categoria abre o
// seletor de subcategoria (e, para Viagens, também a opção de "Lançar Viagem"). Cada botão de
// categoria carrega um checkbox "concluído" no canto — flag manual de progresso deste projeto
// (statusPorCategoriaId/onToggleStatus), independente do clique principal (stopPropagation).
function BotoesCategoriaProjeto({ C, arvore, onSelecionar, onLancarGenerico, statusPorCategoriaId, onToggleStatus }) {
  if (arvore.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
      {arvore.map((paiLinha) => {
        const semSubcategorias = paiLinha.subcategorias.length === 0;
        const concluida = !!statusPorCategoriaId[paiLinha.categoria.id];
        return (
          <button
            key={paiLinha.categoria.id}
            onClick={() => onSelecionar(paiLinha)}
            disabled={semSubcategorias}
            title={semSubcategorias ? "Cadastre uma subcategoria primeiro" : `Lançar em ${paiLinha.categoria.nome}`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              background: concluida ? C.success : C.brand, border: "none", borderRadius: 10,
              padding: "12px 14px", cursor: semSubcategorias ? "not-allowed" : "pointer",
              opacity: semSubcategorias ? 0.5 : 1, textAlign: "left", fontFamily: "inherit",
              boxShadow: `0 2px 8px ${(concluida ? C.success : C.brand)}40`, transition: "background .15s",
            }}
          >
            <span style={{
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.22)", color: "#fff",
            }}>
              <Plus size={15} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{paiLinha.categoria.codigo}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{paiLinha.categoria.nome}</span>
            </span>
            <input
              type="checkbox"
              checked={concluida}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggleStatus(paiLinha.categoria.id, e.target.checked)}
              title={concluida ? "Marcado como concluído — clique para desmarcar" : "Marcar como concluído (já terminei de preencher esta categoria)"}
              style={{ width: 16, height: 16, flexShrink: 0, accentColor: "#fff", cursor: "pointer" }}
            />
          </button>
        );
      })}
    </div>
  );
}

// Escolha da categoria mãe (aberta pelo botão genérico "Lançar Despesa") — depois cai no
// ModalEscolherSubcategoriaProjeto de sempre, já com a categoria escolhida.
function ModalEscolherCategoriaPaiProjeto({ C, arvore, onClose, onEscolher }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Lançar Despesa</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={20} /></button>
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: C.muted }}>Escolha a categoria mãe:</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, padding: 6 }}>
          {arvore.map((paiLinha) => (
            <button
              key={paiLinha.categoria.id}
              type="button"
              onClick={() => onEscolher(paiLinha)}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                background: "transparent", border: "1px solid transparent", borderRadius: 8,
                padding: "10px 12px", cursor: "pointer", fontFamily: "inherit",
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 48, flexShrink: 0 }}>{paiLinha.categoria.codigo}</span>
              <span style={{ fontSize: 13, color: C.text }}>{paiLinha.categoria.nome}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Modal intermediário do botão-KPI: escolhe a subcategoria (filtrada à categoria mãe clicada)
// antes de abrir o modal de item de verdade — a regra de negócio do item não muda, só o caminho
// até ele. Para a categoria de Viagens, o único caminho é o atalho "Lançar Viagem" — as 5
// subcategorias (2.5.01–05, incluindo Verba de Relacionamento) são todas cobertas pelo fluxo de
// grupo, então não há mais opção de lançar item avulso nessa categoria.
// Lista de subcategorias sempre visível (em vez de um combo fechado) — maior área de leitura;
// mesmo com scroll (muitas subcategorias), dá pra ver várias linhas de uma vez.
function ListaSubcategorias({ C, subcategorias, selecionadaId, onSelecionar }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, padding: 6 }}>
      {subcategorias.map((s) => {
        const selecionada = String(selecionadaId) === String(s.categoria.id);
        return (
          <button
            key={s.categoria.id}
            type="button"
            onClick={() => onSelecionar(s.categoria.id)}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
              background: selecionada ? C.bgActive : "transparent",
              border: `1px solid ${selecionada ? C.brand : "transparent"}`,
              borderRadius: 8, padding: "10px 12px", cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 48, flexShrink: 0 }}>{s.categoria.codigo}</span>
            <span style={{ fontSize: 13, color: C.text, fontWeight: selecionada ? 700 : 400 }}>{s.categoria.nome}</span>
          </button>
        );
      })}
    </div>
  );
}

function ModalEscolherSubcategoriaProjeto({ C, paiLinha, onClose, onEscolherSubcategoria, onLancarViagem, onLancarKitAdmissao }) {
  const ehViagens = paiLinha.categoria.codigo === CODIGO_CATEGORIA_VIAGENS_PREFIXO;
  const ehEquipamentos = paiLinha.categoria.codigo === CODIGO_CATEGORIA_EQUIPAMENTOS_PREFIXO;
  const [subcategoriaId, setSubcategoriaId] = useState(paiLinha.subcategorias[0]?.categoria.id ?? "");

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Lançar em {paiLinha.categoria.codigo} {paiLinha.categoria.nome}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={20} /></button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {ehViagens ? (
            <>
              <button onClick={onLancarViagem} style={{ ...btnStyle(C.brand), justifyContent: "center" }}>
                <Plane size={15} /> Lançar Viagem
              </button>
              <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
                Lança de uma vez o custo em Alimentação, Hospedagem, Passagem Aérea, Transporte e Verba de Relacionamento, usando o ticket médio travado do projeto.
              </p>
            </>
          ) : ehEquipamentos ? (
            <>
              <button onClick={onLancarKitAdmissao} style={{ ...btnStyle(C.brand), justifyContent: "center" }}>
                <Laptop size={15} /> Lançar Kit Admissão
              </button>
              <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
                Lança de uma vez Fone, Teclado, Mouse, Notebook (do perfil escolhido) e Monitor — desmarque o que já houver em estoque —, mais o perfil de assinaturas (Google + IA/GitLab opcionais), usando os valores de Premissas · Equipamentos e Materiais.
              </p>
              {paiLinha.subcategorias.length > 0 && (
                <>
                  <p style={{ margin: "6px 0 0", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Ou lance um item avulso:</p>
                  <ListaSubcategorias C={C} subcategorias={paiLinha.subcategorias} selecionadaId={subcategoriaId} onSelecionar={setSubcategoriaId} />
                  <button
                    style={{ ...btnStyle(C.brand, true), justifyContent: "center", opacity: subcategoriaId ? 1 : 0.5 }}
                    onClick={() => onEscolherSubcategoria(subcategoriaId)}
                    disabled={!subcategoriaId}
                  >
                    Continuar
                  </button>
                </>
              )}
            </>
          ) : paiLinha.subcategorias.length > 0 ? (
            <>
              <ListaSubcategorias C={C} subcategorias={paiLinha.subcategorias} selecionadaId={subcategoriaId} onSelecionar={setSubcategoriaId} />
              <button
                style={{ ...btnStyle(C.brand), justifyContent: "center", opacity: subcategoriaId ? 1 : 0.5 }}
                onClick={() => onEscolherSubcategoria(subcategoriaId)}
                disabled={!subcategoriaId}
              >
                Continuar
              </button>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Nenhuma subcategoria cadastrada nesta categoria.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Ticket médio travado por projeto, um por subcategoria de Viagens (2.5.01–04) — só leitura
// aqui (necessário pra estimativa ao vivo do lançamento). Edição migrou para a tela dedicada
// Premissas · Viagens (ver PremissasViagemView.js) — decisão do usuário de não deixar o mesmo
// dado editável em dois lugares diferentes.
function CartaoTicketViagemSomenteLeitura({ C, ticket }) {
  const nomeCurto = ticket.categoria_nome.replace(/^Viagens - /, "");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label style={{ fontSize: 10, color: C.muted }}>{nomeCurto}</label>
      <div style={{ ...inputStyle(C), padding: "6px 8px", opacity: 0.7, background: C.bg }}>
        {ticket.ticket_medio}
      </div>
    </div>
  );
}

// Grade de todas as categorias (pai > subcategoria > itens) de Custos Gerais (OPERAÇÃO) para
// este projeto, sempre visível de uma vez (sem select) — somente leitura + editar/excluir; a
// criação de itens novos passou para os botões-KPI (BotoesCategoriaProjeto), não é mais "+"
// direto na linha da subcategoria.
function TabelaItensProjeto({ C, arvore, onEditarItem, onExcluirItem }) {
  const [colapsadas, setColapsadas] = useState(() => new Set(arvore.map((c) => c.categoria.id)));
  // "2.1"/"2.2" (Mão de Obra) chegam depois, num fetch à parte (getMaoDeObraCustosGeraisProjeto) —
  // no mount `arvore` ainda não tem essas categorias, então o useState acima não as recolhe.
  // Sincroniza toda categoria nova que aparecer depois, recolhida por padrão, sem mexer no que
  // o usuário já expandiu manualmente (só adiciona id nunca visto antes).
  const idsConhecidosRef = useRef(new Set(arvore.map((c) => c.categoria.id)));
  useEffect(() => {
    const novos = arvore.map((c) => c.categoria.id).filter((id) => !idsConhecidosRef.current.has(id));
    if (novos.length === 0) return;
    novos.forEach((id) => idsConhecidosRef.current.add(id));
    setColapsadas((prev) => new Set([...prev, ...novos]));
  }, [arvore]);

  function toggle(categoriaId) {
    setColapsadas((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaId)) next.delete(categoriaId); else next.add(categoriaId);
      return next;
    });
  }

  const totalGeralMensal = useMemo(() => {
    const totais = Array(12).fill(0);
    arvore.forEach((pai) => pai.mensal.forEach((v, i) => { totais[i] += v; }));
    return totais;
  }, [arvore]);

  if (arvore.length === 0) {
    return <p style={{ fontSize: 12, color: C.muted }}>Nenhuma categoria de Custos Gerais cadastrada para a OPERAÇÃO ainda.</p>;
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 480 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...thStyleTree(C, 0), width: COL_ACOES_WIDTH, minWidth: COL_ACOES_WIDTH }}>Ações</th>
              <th style={{ ...thStyleTree(C, COL_ACOES_WIDTH), width: COL_CODIGO_WIDTH, minWidth: COL_CODIGO_WIDTH }}>Código</th>
              <th style={{ ...thStyleTree(C, COL_ACOES_WIDTH + COL_CODIGO_WIDTH), textAlign: "left", width: COL_ITEM_WIDTH, minWidth: COL_ITEM_WIDTH }}>Item</th>
              {MESES_ABREV.map((m) => <th key={m} style={thStyleTree(C)}>{m}</th>)}
              <th style={thStyleTree(C)}>Total</th>
            </tr>
          </thead>
          <tbody>
            {arvore.map((paiLinha) => {
              const paiColapsada = colapsadas.has(paiLinha.categoria.id);
              return (
                <Fragment key={paiLinha.categoria.id}>
                  <tr onClick={() => toggle(paiLinha.categoria.id)} style={{ background: C.categoriaBg, fontWeight: 700, cursor: "pointer" }}>
                    <td style={tdStyleTree(C, true, { left: 0, background: C.categoriaBg })} />
                    <td style={tdStyleTree(C, true, { left: COL_ACOES_WIDTH, background: C.categoriaBg })}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start" }}>
                        {paiColapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                        {paiLinha.categoria.codigo}
                      </div>
                    </td>
                    <td style={{ ...tdStyleTree(C, true, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: C.categoriaBg }), textAlign: "left" }}>
                      {paiLinha.categoria.nome}
                      {paiColapsada && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                          ({paiLinha.subcategorias.length} {paiLinha.subcategorias.length === 1 ? "subcategoria" : "subcategorias"})
                        </span>
                      )}
                    </td>
                    {paiLinha.mensal.map((v, i) => <td key={i} style={tdStyleTree(C)}>{formatCurrency(v)}</td>)}
                    <td style={tdStyleTree(C, true)}>{formatCurrency(paiLinha.total)}</td>
                  </tr>

                  {!paiColapsada && paiLinha.subcategorias.length === 0 && (
                    <tr>
                      <td colSpan={16} style={{ padding: "8px 12px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                        Nenhuma subcategoria cadastrada nesta categoria.
                      </td>
                    </tr>
                  )}

                  {!paiColapsada && paiLinha.subcategorias.map((subLinha) => {
                    // Subcategoria de Mão de Obra (2.1.xx/2.2.xx): valor já vem calculado de
                    // Alocação de Pessoal, não tem itens lançáveis — linha fixa, sem
                    // toggle/expandir nem o aviso de "nenhum item lançado" (não se aplica).
                    if (subLinha.computado) {
                      return (
                        <tr key={subLinha.categoria.id} style={{ background: C.subcategoriaBg, fontWeight: 600 }}>
                          <td style={tdStyleTree(C, true, { left: 0, background: C.subcategoriaBg })} />
                          <td style={tdStyleTree(C, true, { left: COL_ACOES_WIDTH, background: C.subcategoriaBg })}>
                            <div style={{ paddingLeft: 14 }}>{subLinha.categoria.codigo}</div>
                          </td>
                          <td style={{ ...tdStyleTree(C, true, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: C.subcategoriaBg }), textAlign: "left" }}>
                            {subLinha.categoria.nome}
                          </td>
                          {subLinha.mensal.map((v, i) => <td key={i} style={tdStyleTree(C)}>{formatCurrency(v)}</td>)}
                          <td style={tdStyleTree(C, true)}>{formatCurrency(subLinha.total)}</td>
                        </tr>
                      );
                    }
                    const subColapsada = colapsadas.has(subLinha.categoria.id);
                    return (
                      <Fragment key={subLinha.categoria.id}>
                        <tr onClick={() => toggle(subLinha.categoria.id)} style={{ background: C.subcategoriaBg, fontWeight: 600, cursor: "pointer" }}>
                          <td style={tdStyleTree(C, true, { left: 0, background: C.subcategoriaBg })} />
                          <td style={tdStyleTree(C, true, { left: COL_ACOES_WIDTH, background: C.subcategoriaBg })}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start", paddingLeft: 14 }}>
                              {subColapsada ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                              {subLinha.categoria.codigo}
                            </div>
                          </td>
                          <td style={{ ...tdStyleTree(C, true, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: C.subcategoriaBg }), textAlign: "left" }}>
                            {subLinha.categoria.nome}
                            {subColapsada && (
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                                ({subLinha.itens.length} {subLinha.itens.length === 1 ? "item" : "itens"})
                              </span>
                            )}
                          </td>
                          {subLinha.mensal.map((v, i) => <td key={i} style={tdStyleTree(C)}>{formatCurrency(v)}</td>)}
                          <td style={tdStyleTree(C, true)}>{formatCurrency(subLinha.total)}</td>
                        </tr>

                        {!subColapsada && subLinha.itens.length === 0 && (
                          <tr>
                            <td colSpan={16} style={{ padding: "8px 12px 8px 40px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                              Nenhum item lançado nesta subcategoria.
                            </td>
                          </tr>
                        )}

                        {!subColapsada && subLinha.itens.map((item) => (
                          <LinhaItem key={item.id} C={C} item={item} onEditar={onEditarItem} onExcluir={onExcluirItem} />
                        ))}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
              <td style={tdStyleTree(C, true, { left: 0, background: C.card })} />
              <td style={{ ...tdStyleTree(C, true, { left: COL_ACOES_WIDTH, background: C.card }), textAlign: "left" }} colSpan={2}>Total Geral</td>
              {totalGeralMensal.map((v, i) => <td key={i} style={tdStyleTree(C, true)}>{formatCurrency(v)}</td>)}
              <td style={tdStyleTree(C, true)}>{formatCurrency(totalGeralMensal.reduce((a, b) => a + b, 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const MESES_1_12 = Array.from({ length: 12 }, (_, idx) => idx + 1);

function totalItem(item) {
  return MESES_1_12.reduce((acc, mes) => acc + (item.valores_mensais[mes] || 0), 0);
}

// Consolidado: mesma árvore de categorias/subcategorias de cada projeto acima, porém somando os
// itens de TODOS os projetos ativos em vez de mostrar por projeto — visão só leitura, sem
// "novo item" (o lançamento em si só acontece nos accordions de projeto). Substitui a antiga
// tela separada "Custos Gerais" (CustosGeraisTotalizadorView.js), unificando projeto e geral
// numa única visão.
function SecaoConsolidado({ C, ano, categorias, itens, projetos, maoDeObra }) {
  const [colapsadas, setColapsadas] = useState(() => new Set());

  // Recolhe tudo por padrão, incluindo "2.1"/"2.2" (Mão de Obra) — que vêm de um fetch à parte
  // (`maoDeObra` prop) e podem resolver antes ou depois de `categorias`. 1 efeito só, reagindo
  // aos dois, sempre recalcula o conjunto completo: 2 efeitos separados sobrescrevendo o mesmo
  // Set (1 por fonte) tinham uma corrida — o que resolvesse por último apagava o que o outro
  // tinha acabado de adicionar.
  useEffect(() => {
    const idsCategorias = categorias.map((c) => c.id);
    const idsMaoDeObra = (maoDeObra?.filhos || []).map((f) => f.id);
    setColapsadas(new Set([...idsCategorias, ...idsMaoDeObra]));
  }, [categorias, maoDeObra]);

  const projetoNomePorId = useMemo(() => Object.fromEntries(projetos.map((p) => [p.id, p.nome])), [projetos]);
  const idsProjetosAtivos = useMemo(() => new Set(projetos.filter((p) => p.ativo).map((p) => p.id)), [projetos]);
  const itensAtivos = useMemo(() => itens.filter((i) => idsProjetosAtivos.has(i.projeto_id)), [itens, idsProjetosAtivos]);
  const arvoreCategorias = useMemo(() => construirArvoreCategorias(categorias, itensAtivos), [categorias, itensAtivos]);
  // Mão de Obra (2.1/2.2) entra junto do consolidado — agregado do departamento inteiro
  // (todos os projetos, sem rateio), já que aqui é soma de tudo mesmo.
  const arvore = useMemo(
    () => [...arvoreMaoDeObraParaCategorias(maoDeObra), ...arvoreCategorias],
    [maoDeObra, arvoreCategorias]
  );

  const totalGeralMensal = useMemo(() => {
    const totais = Array(12).fill(0);
    arvore.forEach((pai) => pai.mensal.forEach((v, i) => { totais[i] += v; }));
    return totais;
  }, [arvore]);

  function toggle(categoriaId) {
    setColapsadas((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaId)) next.delete(categoriaId); else next.add(categoriaId);
      return next;
    });
  }

  // ── Exportar para Excel (backup/consulta caso o BD apresente problema) ────
  function exportarParaExcel() {
    const linhas = [];
    const linhaBase = (codigo, item, projecaoMensal, total, extra = {}) => ({
      "Código": codigo,
      "Item": item,
      "Projeto": "",
      ...extra,
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, projecaoMensal[i]])),
      "Total": total,
    });

    arvore.forEach((paiLinha) => {
      linhas.push(linhaBase(paiLinha.categoria.codigo, paiLinha.categoria.nome, paiLinha.mensal, paiLinha.total));
      paiLinha.subcategorias.forEach((subLinha) => {
        linhas.push(linhaBase(subLinha.categoria.codigo, `  ${subLinha.categoria.nome}`, subLinha.mensal, subLinha.total));
        subLinha.itens.forEach((item) => {
          const mensalItem = MESES_1_12.map((mes) => item.valores_mensais[mes] || 0);
          linhas.push(linhaBase("", `    ${item.item_nome}`, mensalItem, totalItem(item), {
            "Projeto": projetoNomePorId[item.projeto_id] || "",
          }));
        });
      });
    });
    linhas.push(linhaBase("", "Total Geral", totalGeralMensal, totalGeralMensal.reduce((a, b) => a + b, 0)));

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Consolidado");
    const nomeArquivo = `Consolidado_Custos_Gerais_OPERACAO_${ano}`.replace(/[^\w]+/g, "_");
    XLSX.writeFile(workbook, `${nomeArquivo}.xlsx`);
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Consolidado · OPERAÇÃO · {ano}</h2>
          <span style={{ fontSize: 11, color: C.muted }}>
            Soma de todos os projetos ativos acima — os lançamentos são feitos em cada projeto, aqui é só consulta.
          </span>
        </div>
        <button
          onClick={exportarParaExcel}
          disabled={arvore.length === 0}
          title="Exportar dados para Excel"
          style={{ ...btnStyle(C.success, true), opacity: arvore.length === 0 ? 0.45 : 1, cursor: arvore.length === 0 ? "not-allowed" : "pointer" }}
        >
          <FileSpreadsheet size={15} /> Exportar Excel
        </button>
      </div>

      {arvore.length === 0 ? (
        <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
          Nenhuma categoria de Custos Gerais cadastrada para a OPERAÇÃO ainda.
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyleTree(C, 0), width: COL_CODIGO_WIDTH, minWidth: COL_CODIGO_WIDTH }}>Código</th>
                  <th style={{ ...thStyleTree(C, COL_CODIGO_WIDTH), textAlign: "left", width: COL_ITEM_WIDTH, minWidth: COL_ITEM_WIDTH }}>Item</th>
                  {MESES_ABREV.map((m) => <th key={m} style={thStyleTree(C)}>{m}</th>)}
                  <th style={thStyleTree(C)}>Total</th>
                </tr>
              </thead>
              <tbody>
                {arvore.map((paiLinha) => {
                  const paiColapsada = colapsadas.has(paiLinha.categoria.id);
                  return (
                    <Fragment key={paiLinha.categoria.id}>
                      <tr onClick={() => toggle(paiLinha.categoria.id)} style={{ background: C.categoriaBg, fontWeight: 700, cursor: "pointer" }}>
                        <td style={tdStyleTree(C, true, { left: 0, background: C.categoriaBg })}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start" }}>
                            {paiColapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                            {paiLinha.categoria.codigo}
                          </div>
                        </td>
                        <td style={{ ...tdStyleTree(C, true, { left: COL_CODIGO_WIDTH, background: C.categoriaBg }), textAlign: "left" }}>
                          {paiLinha.categoria.nome}
                          {paiColapsada && (
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                              ({paiLinha.subcategorias.length} {paiLinha.subcategorias.length === 1 ? "subcategoria" : "subcategorias"})
                            </span>
                          )}
                        </td>
                        {paiLinha.mensal.map((v, i) => <td key={i} style={tdStyleTree(C)}>{formatCurrency(v)}</td>)}
                        <td style={tdStyleTree(C, true)}>{formatCurrency(paiLinha.total)}</td>
                      </tr>

                      {!paiColapsada && paiLinha.subcategorias.length === 0 && (
                        <tr>
                          <td colSpan={15} style={{ padding: "8px 12px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                            Nenhuma subcategoria cadastrada nesta categoria.
                          </td>
                        </tr>
                      )}

                      {!paiColapsada && paiLinha.subcategorias.map((subLinha) => {
                        // Subcategoria de Mão de Obra (2.1.xx/2.2.xx): valor já calculado de
                        // Alocação de Pessoal, sem itens lançáveis — linha fixa, sem
                        // toggle/expandir nem o aviso de "nenhum item lançado".
                        if (subLinha.computado) {
                          return (
                            <tr key={subLinha.categoria.id} style={{ background: C.subcategoriaBg, fontWeight: 600 }}>
                              <td style={tdStyleTree(C, true, { left: 0, background: C.subcategoriaBg })}>
                                <div style={{ paddingLeft: 14 }}>{subLinha.categoria.codigo}</div>
                              </td>
                              <td style={{ ...tdStyleTree(C, true, { left: COL_CODIGO_WIDTH, background: C.subcategoriaBg }), textAlign: "left" }}>
                                {subLinha.categoria.nome}
                              </td>
                              {subLinha.mensal.map((v, i) => <td key={i} style={tdStyleTree(C)}>{formatCurrency(v)}</td>)}
                              <td style={tdStyleTree(C, true)}>{formatCurrency(subLinha.total)}</td>
                            </tr>
                          );
                        }
                        const subColapsada = colapsadas.has(subLinha.categoria.id);
                        return (
                          <Fragment key={subLinha.categoria.id}>
                            <tr onClick={() => toggle(subLinha.categoria.id)} style={{ background: C.subcategoriaBg, fontWeight: 600, cursor: "pointer" }}>
                              <td style={tdStyleTree(C, true, { left: 0, background: C.subcategoriaBg })}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start", paddingLeft: 14 }}>
                                  {subColapsada ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                  {subLinha.categoria.codigo}
                                </div>
                              </td>
                              <td style={{ ...tdStyleTree(C, true, { left: COL_CODIGO_WIDTH, background: C.subcategoriaBg }), textAlign: "left" }}>
                                {subLinha.categoria.nome}
                                {subColapsada && (
                                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                                    ({subLinha.itens.length} {subLinha.itens.length === 1 ? "item" : "itens"})
                                  </span>
                                )}
                              </td>
                              {subLinha.mensal.map((v, i) => <td key={i} style={tdStyleTree(C)}>{formatCurrency(v)}</td>)}
                              <td style={tdStyleTree(C, true)}>{formatCurrency(subLinha.total)}</td>
                            </tr>

                            {!subColapsada && subLinha.itens.length === 0 && (
                              <tr>
                                <td colSpan={15} style={{ padding: "8px 12px 8px 40px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                                  Nenhum item lançado nesta subcategoria.
                                </td>
                              </tr>
                            )}

                            {!subColapsada && subLinha.itens.map((item) => (
                              <tr key={item.id}>
                                <td style={tdStyleTree(C, false, { left: 0, background: C.card })}>—</td>
                                <td style={{ ...tdStyleTree(C, false, { left: COL_CODIGO_WIDTH, background: C.card }), textAlign: "left", paddingLeft: 26 }}>
                                  {item.item_nome}
                                  {item.item_grupo_id && item.tipo_lancamento !== "kit_admissao" && (
                                    <Plane size={11} color={C.muted} style={{ marginLeft: 6, verticalAlign: "middle" }} />
                                  )}
                                  {item.tipo_lancamento === "kit_admissao" && (
                                    <Laptop size={11} color={C.muted} style={{ marginLeft: 6, verticalAlign: "middle" }} />
                                  )}
                                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 500, color: C.muted }}>
                                    ({projetoNomePorId[item.projeto_id] || "—"})
                                  </span>
                                </td>
                                {MESES_1_12.map((mes) => (
                                  <td key={mes} style={tdStyleTree(C)}>{formatCurrency(item.valores_mensais[mes] || 0)}</td>
                                ))}
                                <td style={tdStyleTree(C, true)}>{formatCurrency(totalItem(item))}</td>
                              </tr>
                            ))}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                  <td style={{ ...tdStyleTree(C, true, { left: 0, background: C.card }), textAlign: "left" }} colSpan={2}>Total Geral</td>
                  {totalGeralMensal.map((v, i) => <td key={i} style={tdStyleTree(C, true)}>{formatCurrency(v)}</td>)}
                  <td style={tdStyleTree(C, true)}>{formatCurrency(totalGeralMensal.reduce((a, b) => a + b, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// Rótulo de vigência de uma faixa (ex.: "Jan–Mar") — usado no título (tooltip) do item quando
// ele tem mais de 1 período, já que cada item agora é sempre 1 linha só (item.faixas pode ter
// vários períodos não contíguos com valores diferentes, tudo dentro dessa mesma linha).
function rotuloFaixa(faixa) {
  return `${MESES_ABREV[faixa.mes_inicio - 1]}–${MESES_ABREV[faixa.mes_fim - 1]}`;
}

// Só leitura — edição é sempre via modal (ícone de lápis), nunca célula-a-célula na tabela.
function LinhaItem({ C, item, onEditar, onExcluir }) {
  // Todo grupo que não é Kit é Viagem — inclui a Verba e as linhas do modo "Custo total" (valor_manual).
  const ehGrupoViagem = !!item.item_grupo_id && item.tipo_lancamento !== "kit_admissao";
  const ehGrupoKit = item.tipo_lancamento === "kit_admissao";
  const multiplasFaixas = item.faixas.length > 1;
  return (
    <tr>
      <td style={tdStyleTree(C, false, { left: 0, background: C.card })}>
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <button onClick={() => onEditar(item)} style={{ background: "none", border: "none", cursor: "pointer", color: C.text, display: "inline-flex" }} title={ehGrupoViagem ? "Editar lançamento de viagem (afeta as 4 subcategorias)" : ehGrupoKit ? "Editar Kit Admissão (afeta todas as linhas do kit)" : "Editar item"}>
            <Pencil size={13} />
          </button>
          <button onClick={() => onExcluir(item)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, display: "inline-flex" }} title={ehGrupoViagem ? "Excluir lançamento de viagem (afeta as 4 subcategorias)" : ehGrupoKit ? "Excluir Kit Admissão (afeta todas as linhas do kit)" : "Excluir item"}>
            <Trash2 size={13} />
          </button>
        </div>
      </td>
      <td style={tdStyleTree(C, false, { left: COL_ACOES_WIDTH, background: C.card })}>—</td>
      <td
        style={{ ...tdStyleTree(C, false, { left: COL_ACOES_WIDTH + COL_CODIGO_WIDTH, background: C.card }), textAlign: "left", paddingLeft: 26 }}
        title={multiplasFaixas ? item.faixas.map(rotuloFaixa).join(" · ") : undefined}
      >
        {item.item_nome}
        {ehGrupoViagem && (
          <Plane size={11} color={C.muted} style={{ marginLeft: 6, verticalAlign: "middle" }} />
        )}
        {ehGrupoKit && (
          <Laptop size={11} color={C.muted} style={{ marginLeft: 6, verticalAlign: "middle" }} />
        )}
        {multiplasFaixas && (
          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 500, color: C.muted }}>({item.faixas.length} períodos)</span>
        )}
      </td>
      {MESES_1_12.map((mes) => (
        <td key={mes} style={tdStyleTree(C)}>{formatCurrency(item.valores_mensais[mes] || 0)}</td>
      ))}
      <td style={tdStyleTree(C, true)}>{formatCurrency(totalItem(item))}</td>
    </tr>
  );
}

function faixaItemVazia() {
  return { mes_inicio: 1, mes_fim: 12, valor: 0, valor_unitario: 0, usuarios: 0 };
}

// Um item pode ter várias faixas de vigência com valores diferentes (ex.: Jan–Mar com um
// valor, Jun–Out com outro) — tudo dentro da MESMA linha (mesmo id); "Adicionar período" nunca
// cria um item novo (ver abrirEdicaoItem/criarItem/atualizarItem no componente pai).
function ModalItem({ C, ano, projetoId, categoria, itemExistente, onClose, onCriar, onAtualizar }) {
  const editando = !!itemExistente;
  const tiposPermitidos = tiposPermitidosParaCategoria(categoria);
  const especial = categoriaEhEspecial(categoria);

  const [itemNome, setItemNome] = useState(itemExistente?.item_nome || "");
  const [tipoLancamento, setTipoLancamento] = useState(itemExistente?.tipo_lancamento || "valor_manual");
  const [faixas, setFaixas] = useState(() => (
    itemExistente?.faixas?.length ? itemExistente.faixas.map((f) => ({ ...f })) : [faixaItemVazia()]
  ));
  const [reajustes, setReajustes] = useState(() => reajustesIniciais(itemExistente));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  function atualizarFaixa(idx, chave, valor) {
    setFaixas((fs) => fs.map((f, i) => {
      if (i !== idx) return f;
      const nova = { ...f, [chave]: valor };
      if (chave === "mes_inicio" && Number(valor) > Number(nova.mes_fim)) nova.mes_fim = valor;
      return nova;
    }));
  }

  function adicionarFaixa() {
    setFaixas((fs) => {
      const ultimoFim = fs.length ? Number(fs[fs.length - 1].mes_fim) : 0;
      const proximoInicio = Math.min(ultimoFim + 1, 12);
      return [...fs, { ...faixaItemVazia(), mes_inicio: proximoInicio, mes_fim: 12 }];
    });
  }

  function removerFaixa(idx) {
    setFaixas((fs) => (fs.length > 1 ? fs.filter((_, i) => i !== idx) : fs));
  }

  async function salvar() {
    if (!categoria) return;
    if (!itemNome.trim()) {
      setErro("Informe o nome do item.");
      return;
    }
    if (temReajusteIncompleto(reajustes)) {
      setErro("Preencha Tipo, Mês e Percentual em todos os reajustes (ou remova a linha incompleta).");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      const dados = {
        projeto_id: projetoId, categoria_id: categoria.id, item_nome: itemNome.trim(),
        ano_referencia: ano, tipo_lancamento: tipoLancamento,
        faixas: faixas.map((f) => ({
          mes_inicio: Number(f.mes_inicio), mes_fim: Number(f.mes_fim),
          valor: Number(f.valor) || 0, valor_unitario: Number(f.valor_unitario) || 0, usuarios: Number(f.usuarios) || 0,
        })),
        reajustes: reajustesParaPayload(reajustes),
      };
      if (editando) await onAtualizar(itemExistente.id, dados);
      else await onCriar(dados);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editando ? "Editar item de custo" : "Novo item de custo"}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={20} /></button>
        </div>

        {erro && (
          <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: C.subcategoriaBg, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
            Categoria: <strong>{categoria ? `${categoria.codigo} ${categoria.nome}` : "—"}</strong>
          </div>
          <Campo label="Nome do item" C={C}>
            <input value={itemNome} onChange={(e) => setItemNome(e.target.value)} placeholder={categoria.nome} style={inputStyle(C)} />
          </Campo>

          {especial && (
            <Campo label="Tipo de lançamento" C={C}>
              <select value={tipoLancamento} onChange={(e) => setTipoLancamento(e.target.value)} style={inputStyle(C)}>
                {TIPOS_LANCAMENTO_OPTIONS.filter((o) => tiposPermitidos.includes(o.value)).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: C.muted }}>
                {TIPOS_LANCAMENTO_OPTIONS.find((o) => o.value === tipoLancamento)?.descricao}
              </span>
            </Campo>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {faixas.map((faixa, idx) => (
              <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Período {idx + 1}</span>
                  {faixas.length > 1 && (
                    <button onClick={() => removerFaixa(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, display: "inline-flex" }} title="Remover este período">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                {(tipoLancamento === "valor_manual" || tipoLancamento === "alocacao_projeto") && (
                  <Campo label={tipoLancamento === "alocacao_projeto" ? "Ticket por pessoa (R$)" : "Valor (R$)"} C={C}>
                    <input type="number" value={faixa.valor} onChange={(e) => atualizarFaixa(idx, "valor", e.target.value)} style={inputStyle(C)} />
                  </Campo>
                )}

                {tipoLancamento === "quantidade_usuarios" && (
                  <div style={{ display: "flex", gap: 12 }}>
                    <Campo label="Valor unitário (R$)" C={C}>
                      <input type="number" value={faixa.valor_unitario} onChange={(e) => atualizarFaixa(idx, "valor_unitario", e.target.value)} style={inputStyle(C)} />
                    </Campo>
                    <Campo label="Nº usuários" C={C}>
                      <input type="number" value={faixa.usuarios} onChange={(e) => atualizarFaixa(idx, "usuarios", e.target.value)} style={inputStyle(C)} />
                    </Campo>
                  </div>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                  <Campo label="Mês início" C={C}>
                    <select value={faixa.mes_inicio} onChange={(e) => atualizarFaixa(idx, "mes_inicio", Number(e.target.value))} style={inputStyle(C)}>
                      {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Mês fim" C={C}>
                    <select value={faixa.mes_fim} onChange={(e) => atualizarFaixa(idx, "mes_fim", Number(e.target.value))} style={inputStyle(C)}>
                      {MESES_ABREV.map((m, i) => i + 1 >= faixa.mes_inicio && <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </Campo>
                </div>
              </div>
            ))}
          </div>

          <button onClick={adicionarFaixa} style={{ ...btnStyle(C.brand, true), alignSelf: "flex-start", padding: "6px 12px", fontSize: 12 }}>
            <Plus size={13} /> Adicionar período
          </button>

          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Cada período vale, sem dividir, em todo mês de "Mês início" a "Mês fim" — use mais de um período quando o valor mudar ao longo do ano (ex.: Jan–Mar com um valor, Jun–Out com outro).
          </p>

          <ReajustesFaixasEditor C={C} reajustes={reajustes} onChange={setReajustes} />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }} onClick={onClose}>Cancelar</button>
          <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }} onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : editando ? "Salvar alterações" : "Criar item"}
          </button>
        </div>
      </div>
    </div>
  );
}

function faixaViagemVazia() {
  return { mes_inicio: 1, mes_fim: 12, n_pessoas: 1, n_viagens: 1, n_dias: 2, reajuste_pct: 0, verba_relacionamento: 0, valores_subcategoria: {} };
}

// "Lançar Kit Admissão" — 1 lançamento cria até 5 linhas (Headset/Teclado/Mouse +
// Notebook/Monitor do perfil escolhido, só os marcados) em 2.7.01, usando o ticket já cadastrado em Premissas ·
// Equipamentos e Materiais (sem ticket próprio, ao contrário de Viagem). Vale para 1 mês só
// (compra pontual), com quantidade sugerida a partir de Mão de Obra (contratações do mês − kits
// já lançados).
function ModalGrupoKitAdmissao({ C, ano, projetoId, grupoExistente, onClose, onCriar, onAtualizar }) {
  const editando = !!grupoExistente;

  const [tipoKit, setTipoKit] = useState(grupoExistente?.tipo_kit || "atendimento");
  const [mes, setMes] = useState(grupoExistente?.mes || 1);
  const [quantidade, setQuantidade] = useState(grupoExistente?.quantidade ?? 1);
  const [itemNome, setItemNome] = useState(grupoExistente?.item_nome || "");
  const [assinaturas, setAssinaturas] = useState(grupoExistente?.assinaturas || []);
  const [equipamentos, setEquipamentos] = useState(grupoExistente?.equipamentos || EQUIPAMENTOS_KIT_ADMISSAO_PADRAO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [tickets, setTickets] = useState([]);
  const [sugestao, setSugestao] = useState(null);

  useEffect(() => {
    Promise.all([getEquipamentosTicketMedio(ano), getAssinaturasTicketMedio(ano)])
      .then(([equipamentos, assinaturas]) => setTickets([...equipamentos, ...assinaturas]))
      .catch(() => {});
  }, [ano]);

  // Sugestão só faz sentido ao criar (editando já vem com a quantidade real lançada) — recalcula
  // sempre que o mês muda, contratações do mês OPERAÇÃO menos kits já lançados naquele mês.
  useEffect(() => {
    if (editando) return;
    getReconciliacaoKitsAdmissao(ano)
      .then((linhas) => {
        const linhaOperacao = linhas.find((l) => l.departamento === DEPARTAMENTO_OPERACAO);
        const linhaMes = linhaOperacao?.meses.find((m) => m.mes === mes);
        const sugerido = Math.max(0, (linhaMes?.contratacoes || 0) - (linhaMes?.kits_lancados || 0));
        setSugestao(linhaMes ? { ...linhaMes, sugerido } : null);
        setQuantidade(Math.max(1, sugerido));  // sem contratação pendente ainda sugere 1 — kit tem mínimo de 1
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, ano, editando]);

  const ticketPorCodigo = useMemo(() => Object.fromEntries(tickets.map((t) => [t.codigo, t.ticket_medio])), [tickets]);
  const equipPorCodigo = useMemo(() => Object.fromEntries(tickets.map((t) => [t.codigo, t])), [tickets]);
  const estimativa = custoEquipamentosKit(tipoKit, equipamentos, ticketPorCodigo) * Number(quantidade || 0);
  const estimativaAssinaturas = estimativaAssinaturasKit(assinaturas, ticketPorCodigo, quantidade, mes);

  async function salvar() {
    if (!(Number(quantidade) >= 1)) {
      setErro("Quantidade de kits deve ser no mínimo 1.");
      return;
    }
    if (equipamentos.length === 0 && assinaturas.length === 0) {
      setErro("Selecione ao menos um equipamento ou uma assinatura opcional — só a Google não fica visível aqui para editar depois.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      const dados = { tipo_kit: tipoKit, mes: Number(mes), quantidade: Number(quantidade), item_nome: itemNome.trim() || null, assinaturas, equipamentos };
      if (editando) await onAtualizar(grupoExistente.item_grupo_id, dados);
      else await onCriar({ projeto_id: projetoId, ano_referencia: ano, ...dados });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Laptop size={16} color={C.brand} />
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editando ? "Editar Kit Admissão" : "Lançar Kit Admissão"}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={20} /></button>
        </div>

        {erro && (
          <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Equipamentos marcados (compra no mês escolhido) + assinaturas por usuário (mensais, do mês escolhido até Dez), usando os valores de Premissas · Equipamentos e Materiais.
          </p>

          <Campo label="Equipamentos (por kit)" C={C}>
            <EquipamentosKit C={C} equipamentos={equipamentos} onChange={setEquipamentos} tipoKit={tipoKit} onTipoKit={setTipoKit} ticketPorCodigo={ticketPorCodigo} equipPorCodigo={equipPorCodigo} />
          </Campo>

          <Campo label="Perfil de assinaturas (por usuário)" C={C}>
            <PerfilAssinaturasKit C={C} selecionadas={assinaturas} onChange={setAssinaturas} ticketPorCodigo={ticketPorCodigo} mes={mes} />
          </Campo>

          <div style={{ display: "flex", gap: 12 }}>
            <Campo label="Mês" C={C}>
              <select value={mes} onChange={(e) => setMes(Number(e.target.value))} style={inputStyle(C)}>
                {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </Campo>
            <Campo label="Quantidade de kits" C={C}>
              <input type="number" min={1} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} style={inputStyle(C)} />
            </Campo>
          </div>

          {sugestao && (
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
              Contratações da OPERAÇÃO em {MESES_ABREV[mes - 1]}: <strong>{sugestao.contratacoes}</strong> · Kits já lançados: <strong>{sugestao.kits_lancados}</strong> · Sugestão: <strong>{sugestao.sugerido}</strong>
            </p>
          )}

          <Campo label="Nome/observação (opcional)" C={C}>
            <input value={itemNome} onChange={(e) => setItemNome(e.target.value)} placeholder="Ex: Admissões de Setembro" style={inputStyle(C)} />
          </Campo>

          {tickets.length > 0 && (
            <div style={{ background: C.subcategoriaBg, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
              <div>Equipamentos em {MESES_ABREV[mes - 1]}: <strong>{formatCurrency(estimativa)}</strong></div>
            <div style={{ marginTop: 2 }}>
              Assinaturas: <strong>{formatCurrency(estimativaAssinaturas.mensalPorUsuario * Number(quantidade || 0))}</strong>/mês × {estimativaAssinaturas.meses} {estimativaAssinaturas.meses === 1 ? "mês" : "meses"} = <strong>{formatCurrency(estimativaAssinaturas.total)}</strong>
            </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }} onClick={onClose}>Cancelar</button>
          <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }} onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : editando ? "Salvar alterações" : "Lançar kit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 1 lançamento aqui = uma linha por subcategoria de Viagens (Alimentação, Hospedagem,
// Transporte sempre; Passagem Aérea só se o checkbox "Incluir Passagem Aérea" estiver marcado —
// nem toda viagem depende de passagem aérea, ex.: deslocamento terrestre), usando o ticket médio
// já travado no projeto — não pede ticket_medio nem categoria (ver TicketsViagemPanel para
// editar o ticket, admin-only). "Adicionar período" nunca cria linha nova, só mais uma faixa
// dentro dessas linhas — várias faixas = nº de pessoas/viagens/dias diferente por período (ex.:
// 2 pessoas/1 viagem/3 dias em Fev–Mar, 4 pessoas/2 viagens/5 dias em Jul).
function ModalGrupoViagem({ C, ano, projetoId, grupoExistente, onClose, onCriar, onAtualizar }) {
  const editando = !!grupoExistente;

  const [itemNome, setItemNome] = useState(grupoExistente?.item_nome || "");
  const [faixas, setFaixas] = useState(() => (
    grupoExistente?.faixas?.length ? grupoExistente.faixas.map((f) => ({ ...f })) : [faixaViagemVazia()]
  ));
  const [incluirPassagemAerea, setIncluirPassagemAerea] = useState(grupoExistente?.incluirPassagemAerea ?? true);
  const [incluirVerbaRelacionamento, setIncluirVerbaRelacionamento] = useState(grupoExistente?.incluirVerbaRelacionamento ?? false);
  const [modoValor, setModoValor] = useState(grupoExistente?.modoValor || "ticket");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    getTicketsViagemProjeto(projetoId, ano).then(setTickets).catch(() => {});
  }, [projetoId, ano]);

  function atualizarFaixa(idx, chave, valor) {
    setFaixas((fs) => fs.map((f, i) => {
      if (i !== idx) return f;
      const nova = { ...f, [chave]: valor };
      if (chave === "mes_inicio" && Number(valor) > Number(nova.mes_fim)) nova.mes_fim = valor;
      return nova;
    }));
  }

  function adicionarFaixa() {
    setFaixas((fs) => {
      const ultimoFim = fs.length ? Number(fs[fs.length - 1].mes_fim) : 0;
      const proximoInicio = Math.min(ultimoFim + 1, 12);
      return [...fs, { ...faixaViagemVazia(), mes_inicio: proximoInicio, mes_fim: 12 }];
    });
  }

  function removerFaixa(idx) {
    setFaixas((fs) => (fs.length > 1 ? fs.filter((_, i) => i !== idx) : fs));
  }

  function estimativaFaixa(faixa) {
    const verba = incluirVerbaRelacionamento ? Number(faixa.verba_relacionamento || 0) : 0;
    if (modoValor === "valor_total") return somaValorTotalFaixa(faixa, incluirPassagemAerea) + verba;
    const fatorBase = Number(faixa.n_viagens || 0) * Number(faixa.n_pessoas || 0) * (1 + Number(faixa.reajuste_pct || 0));
    const dias = Number(faixa.n_dias || 1);
    const totalTicket = tickets.reduce((acc, t) => {
      if (t.categoria_codigo === CODIGO_CATEGORIA_PASSAGEM_AEREA && !incluirPassagemAerea) return acc;
      const fator = CODIGOS_SUBCATEGORIAS_VIAGEM_DIAS.includes(t.categoria_codigo) ? fatorBase * dias : fatorBase;
      return acc + t.ticket_medio * fator;
    }, 0);
    return totalTicket + verba;
  }

  async function salvar() {
    if (!itemNome.trim()) {
      setErro("Informe o nome do lançamento.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      const dados = {
        item_nome: itemNome.trim(),
        incluir_passagem_aerea: incluirPassagemAerea,
        incluir_verba_relacionamento: incluirVerbaRelacionamento,
        modo_valor: modoValor,
        faixas: faixas.map((f) => ({
          mes_inicio: Number(f.mes_inicio), mes_fim: Number(f.mes_fim),
          n_pessoas: Number(f.n_pessoas) || 0, n_viagens: Number(f.n_viagens) || 0,
          n_dias: Number(f.n_dias) || 1, reajuste_pct: Number(f.reajuste_pct) || 0,
          verba_relacionamento: Number(f.verba_relacionamento) || 0,
          valores_subcategoria: modoValor === "valor_total" ? valoresSubcategoriaPayload(f, incluirPassagemAerea) : {},
        })),
      };
      if (editando) await onAtualizar(grupoExistente.item_grupo_id, dados);
      else await onCriar({ projeto_id: projetoId, ano_referencia: ano, ...dados });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 55, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Plane size={16} color={C.brand} />
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editando ? "Editar Viagem" : "Lançar Viagem"}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={20} /></button>
        </div>

        {erro && (
          <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Lança de uma vez o custo nas subcategorias de Viagens (Alimentação, Hospedagem, Passagem Aérea e Transporte), usando o ticket médio travado no projeto abaixo — Verba de Relacionamento é opcional e não usa ticket médio. Sem ticket/pessoas/viagens? Escolha "Custo total" e informe só o valor.
          </p>

          <Campo label="Como informar o custo" C={C}>
            <SeletorModoValorViagem C={C} modo={modoValor} onChange={setModoValor} />
          </Campo>

          {modoValor === "ticket" && tickets.length > 0 && (
            <div style={{ background: C.subcategoriaBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Ticket Médio de Viagem (travado)
                </span>
                <span style={{ fontSize: 10, color: C.muted }}>Somente leitura — edite em Premissas · Viagens</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                {tickets.map((t) => (
                  <CartaoTicketViagemSomenteLeitura key={t.categoria_id} C={C} ticket={t} />
                ))}
              </div>
            </div>
          )}

          <Campo label="Nome do lançamento" C={C}>
            <input value={itemNome} onChange={(e) => setItemNome(e.target.value)} placeholder="Ex: Missão de campo — Setembro" style={inputStyle(C)} />
          </Campo>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer" }}>
            <input type="checkbox" checked={incluirPassagemAerea} onChange={(e) => setIncluirPassagemAerea(e.target.checked)} />
            Incluir Passagem Aérea neste lançamento
          </label>
          {!incluirPassagemAerea && (
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
              Desmarcado: esta viagem não terá custo de Passagem Aérea (ex.: deslocamento terrestre).
            </p>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer" }}>
            <input type="checkbox" checked={incluirVerbaRelacionamento} onChange={(e) => setIncluirVerbaRelacionamento(e.target.checked)} />
            Incluir Verba de Relacionamento neste lançamento (opcional)
          </label>
          {incluirVerbaRelacionamento && (
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
              Valor manual, não usa ticket médio — informe o valor por período abaixo.
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {faixas.map((faixa, idx) => (
              <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>Período {idx + 1}</span>
                  {faixas.length > 1 && (
                    <button onClick={() => removerFaixa(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, display: "inline-flex" }} title="Remover este período">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                {modoValor === "ticket" ? (
                  <>
                    <div style={{ display: "flex", gap: 12 }}>
                      <Campo label="Nº pessoas" C={C}>
                        <input type="number" min={0} value={faixa.n_pessoas} onChange={(e) => atualizarFaixa(idx, "n_pessoas", e.target.value)} style={inputStyle(C)} />
                      </Campo>
                      <Campo label="Nº viagens" C={C}>
                        <input type="number" min={0} value={faixa.n_viagens} onChange={(e) => atualizarFaixa(idx, "n_viagens", e.target.value)} style={inputStyle(C)} />
                      </Campo>
                      <Campo label="Nº dias" C={C}>
                        <input type="number" min={1} value={faixa.n_dias ?? 1} onChange={(e) => atualizarFaixa(idx, "n_dias", e.target.value)} style={inputStyle(C)} />
                      </Campo>
                    </div>
                    <p style={{ margin: 0, fontSize: 10, color: C.muted }}>
                      Nº dias multiplica o custo de Alimentação e Hospedagem (não afeta Passagem Aérea/Transporte).
                    </p>
                  </>
                ) : (
                  <CamposValorTotalViagem C={C} faixa={faixa} incluirPassagemAerea={incluirPassagemAerea} inputStyle={inputStyle(C)} onChange={(valores) => atualizarFaixa(idx, "valores_subcategoria", valores)} />
                )}

                {incluirVerbaRelacionamento && (
                  <Campo label="Verba de Relacionamento (R$/mês, manual)" C={C}>
                    <input type="number" min={0} step="0.01" value={faixa.verba_relacionamento ?? 0} onChange={(e) => atualizarFaixa(idx, "verba_relacionamento", e.target.value)} style={inputStyle(C)} />
                  </Campo>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                  <Campo label="Mês início" C={C}>
                    <select value={faixa.mes_inicio} onChange={(e) => atualizarFaixa(idx, "mes_inicio", Number(e.target.value))} style={inputStyle(C)}>
                      {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Mês fim" C={C}>
                    <select value={faixa.mes_fim} onChange={(e) => atualizarFaixa(idx, "mes_fim", Number(e.target.value))} style={inputStyle(C)}>
                      {MESES_ABREV.map((m, i) => i + 1 >= faixa.mes_inicio && <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </Campo>
                </div>

                {(modoValor === "valor_total" || tickets.length > 0 || incluirVerbaRelacionamento) && (
                  <div style={{ background: C.subcategoriaBg, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
                    Custo estimado por mês neste período: <strong>{formatCurrency(estimativaFaixa(faixa))}</strong>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={adicionarFaixa} style={{ ...btnStyle(C.brand, true), alignSelf: "flex-start", padding: "6px 12px", fontSize: 12 }}>
            <Plus size={13} /> Adicionar período
          </button>

          <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
            Cada período vale, sem dividir, em todo mês de "Mês início" a "Mês fim" — use mais de um período quando o nº de pessoas/viagens (ou o custo) mudar ao longo do ano.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }} onClick={onClose}>Cancelar</button>
          <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }} onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : editando ? "Salvar alterações" : "Lançar viagem"}
          </button>
        </div>
      </div>
    </div>
  );
}
